/**
 * Platform Services ("IO Services") — the curated marketplace of
 * services InteriorIO itself offers to its customer studios. Lives
 * in a TOP-LEVEL Firestore collection `platformServices` (NOT
 * org-scoped) because the same catalogue is shown to every studio.
 *
 * Distinct from `serviceLibrary` (org-scoped catalogue of the
 * studio's OWN billable services that go on quotes) — different
 * surface, different audience.
 *
 * Write permissions are gated on the `role: 'app_owner'` Firebase
 * Auth custom claim (granted by `scripts/grant-app-owner.ts`). Reads
 * are open to any authenticated user so studios can browse.
 *
 * Schema is intentionally narrow at v1: title + description +
 * category + a `priceLabel` string (the user picked
 * inquiry/request-a-quote, so there's no Razorpay integration —
 * `priceLabel` is plain-text marketing copy like "Starting ₹2,000"
 * or "On request"). When we later add online payments, we add an
 * optional `basePrice` number + `paymentMode` enum without breaking
 * existing docs.
 */
import {
  addDoc,
  collection,
  deleteDoc,
  deleteField,
  doc,
  serverTimestamp,
  updateDoc,
} from 'firebase/firestore';

import { db } from '@/lib/firebase';

// ───── Categories ─────────────────────────────────────────────

export type PlatformServiceCategory =
  | 'site'         // Floor protection, pest control, deep cleaning, AC install…
  | 'marketing'    // Website, logo, photography, social media, SEO…
  | 'business'     // GST filing, legal templates, accounting, domain email…
  | 'materials'    // Logistics, sample procurement, vendor verification…
  | 'other';

export type PlatformServiceCategoryConfig = {
  key: PlatformServiceCategory;
  label: string;
  icon: string;       // Phosphor icon class
  color: string;      // Pill text hex
  tintBg: string;     // Pill background hex
  blurb: string;      // 1-line section subtitle
};

export const PLATFORM_SERVICE_CATEGORIES: PlatformServiceCategoryConfig[] = [
  {
    key: 'site',
    label: 'Site execution',
    icon: 'ph ph-hammer',
    color: '#D97706',
    tintBg: '#FEF3C7',
    blurb: 'On-ground crews for protection, cleaning, install, and pest control.',
  },
  {
    key: 'marketing',
    label: 'Marketing & branding',
    icon: 'ph ph-megaphone',
    color: '#7C3AED',
    tintBg: '#F5F3FF',
    blurb: 'Websites, photoshoots, social media — built for interior studios.',
  },
  {
    key: 'business',
    label: 'Business ops',
    icon: 'ph ph-briefcase',
    color: '#2563EB',
    tintBg: '#DBEAFE',
    blurb: 'Accounting, GST, legal contracts, email — sorted by specialists.',
  },
  {
    key: 'materials',
    label: 'Materials & vendors',
    icon: 'ph ph-truck',
    color: '#059669',
    tintBg: '#D1FAE5',
    blurb: 'Sample procurement, vendor checks, last-mile material delivery.',
  },
  {
    key: 'other',
    label: 'Other',
    icon: 'ph ph-dots-three',
    color: '#6B7280',
    tintBg: '#F3F4F6',
    blurb: 'Specialty services tailored to your studio.',
  },
];

export function getPlatformServiceCategoryConfig(
  key: PlatformServiceCategory | string,
): PlatformServiceCategoryConfig {
  return (
    PLATFORM_SERVICE_CATEGORIES.find((c) => c.key === key) ??
    PLATFORM_SERVICE_CATEGORIES[PLATFORM_SERVICE_CATEGORIES.length - 1]
  );
}

// ───── Item shape ─────────────────────────────────────────────

export type PlatformService = {
  id: string;
  title: string;
  category: PlatformServiceCategory;
  /** Short hook line shown directly under the title — "One call.
   *  We take care of everything." Different from `shortDescription`
   *  which is the explanatory teaser. */
  tagline?: string;
  /** 1-line marketing teaser for the card. */
  shortDescription: string;
  /** Optional longer paragraph for the order modal / detail. */
  longDescription?: string;
  /** Plain-text price label — "Starting ₹2,000", "On request",
   *  "₹3,500 per visit". v1 is inquiry-only so this is marketing
   *  copy, not a billable number. */
  priceLabel: string;
  /** Optional sub-detail under price — "Material + installation
   *  included", "Per visit, excl. GST". */
  priceNote?: string;
  /** Optional numeric hint (used when the admin later wants to
   *  enable online payment for a specific service). Not surfaced
   *  in v1. */
  basePrice?: number;
  /** Optional bullet list of value props. */
  features?: string[];
  /** Optional turnaround copy — "Within 24 hrs", "2-3 days". */
  deliveryTime?: string;
  /** Optional marketing image URL (R2 / public). Kept for
   *  backwards compatibility with v1 docs — new docs use
   *  `images[0]` instead. */
  imageUrl?: string;
  /** Up to 5 marketing image URLs. First image is shown as the
   *  card thumbnail; all are stacked in the detail carousel. */
  images?: string[];
  /** Soft-disable flag. Disabled services hide from the studio
   *  browse view but stay visible in the admin catalogue. */
  isActive: boolean;
  /** Display order within a category (ascending — lower first). */
  sortOrder: number;
  createdBy: string;
  createdAt: { toMillis(): number } | null;
  updatedAt?: { toMillis(): number } | null;
};

// ───── Writes (App Owner only — gated by Firestore rules) ─────

export type CreatePlatformServiceInput = {
  title: string;
  category: PlatformServiceCategory;
  tagline?: string;
  shortDescription: string;
  longDescription?: string;
  priceLabel: string;
  priceNote?: string;
  basePrice?: number;
  features?: string[];
  deliveryTime?: string;
  imageUrl?: string;
  images?: string[];
  isActive: boolean;
  sortOrder: number;
  createdBy: string;
};

export async function createPlatformService(
  input: CreatePlatformServiceInput,
): Promise<string> {
  const payload: Record<string, unknown> = {
    title: input.title.trim(),
    category: input.category,
    shortDescription: input.shortDescription.trim(),
    priceLabel: input.priceLabel.trim(),
    isActive: input.isActive,
    sortOrder: input.sortOrder,
    createdBy: input.createdBy,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };
  const tagline = input.tagline?.trim();
  if (tagline) payload.tagline = tagline;
  const long = input.longDescription?.trim();
  if (long) payload.longDescription = long;
  const pn = input.priceNote?.trim();
  if (pn) payload.priceNote = pn;
  if (input.basePrice != null && input.basePrice > 0) {
    payload.basePrice = input.basePrice;
  }
  if (input.features && input.features.length) {
    payload.features = input.features.map((f) => f.trim()).filter(Boolean);
  }
  const dt = input.deliveryTime?.trim();
  if (dt) payload.deliveryTime = dt;
  const img = input.imageUrl?.trim();
  if (img) payload.imageUrl = img;
  if (input.images && input.images.length) {
    const cleaned = input.images.map((s) => s.trim()).filter(Boolean).slice(0, 5);
    if (cleaned.length) payload.images = cleaned;
  }

  const ref = await addDoc(collection(db, 'platformServices'), payload);
  return ref.id;
}

export async function updatePlatformService(
  id: string,
  data: Partial<Omit<CreatePlatformServiceInput, 'createdBy'>>,
): Promise<void> {
  const updates: Record<string, unknown> = { updatedAt: serverTimestamp() };
  if (data.title !== undefined) updates.title = data.title.trim();
  if (data.category !== undefined) updates.category = data.category;
  if (data.shortDescription !== undefined) {
    updates.shortDescription = data.shortDescription.trim();
  }
  if (data.priceLabel !== undefined) updates.priceLabel = data.priceLabel.trim();
  if (data.isActive !== undefined) updates.isActive = data.isActive;
  if (data.sortOrder !== undefined) updates.sortOrder = data.sortOrder;
  if (data.tagline !== undefined) {
    const v = data.tagline.trim();
    updates.tagline = v || deleteField();
  }
  if (data.longDescription !== undefined) {
    const v = data.longDescription.trim();
    updates.longDescription = v || deleteField();
  }
  if (data.priceNote !== undefined) {
    const v = data.priceNote.trim();
    updates.priceNote = v || deleteField();
  }
  if (data.basePrice !== undefined) {
    updates.basePrice = data.basePrice > 0 ? data.basePrice : deleteField();
  }
  if (data.features !== undefined) {
    const cleaned = data.features.map((f) => f.trim()).filter(Boolean);
    updates.features = cleaned.length ? cleaned : deleteField();
  }
  if (data.deliveryTime !== undefined) {
    const v = data.deliveryTime.trim();
    updates.deliveryTime = v || deleteField();
  }
  if (data.imageUrl !== undefined) {
    const v = data.imageUrl.trim();
    updates.imageUrl = v || deleteField();
  }
  if (data.images !== undefined) {
    const cleaned = data.images.map((s) => s.trim()).filter(Boolean).slice(0, 5);
    updates.images = cleaned.length ? cleaned : deleteField();
  }
  await updateDoc(doc(db, 'platformServices', id), updates);
}

export async function deletePlatformService(id: string): Promise<void> {
  await deleteDoc(doc(db, 'platformServices', id));
}
